import React from 'react';
import Banner from '../../../common/Banner';
import type { ChartDraft } from '../../../../services/offline/chartDrafts';
import { getEntryTitle } from '../utils';

/** "10:42 AM" for the banner */
export const formatDraftTime = (iso: string): string => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

/** Phase 18U U9: drafts of an edit are kept under `edit~<entryId>` */
export const EDIT_DRAFT_PREFIX = 'edit~';
export const editDraftKey = (entryId: string | number) => `${EDIT_DRAFT_PREFIX}${entryId}`;
export const isEditDraft = (entryType: string) => entryType.startsWith(EDIT_DRAFT_PREFIX);

const label = (entryType: string) => (isEditDraft(entryType) ? 'changes to this entry' : getEntryTitle(entryType).toLowerCase());

export interface ChartDraftBannerProps {
  /** The open form's saved draft (Resume / Discard) */
  pendingDraft?: ChartDraft | null;
  onResume?: () => void;
  onDiscard?: () => void;
  /** With no form open: drafts for the visit, each reopened by `onOpenDraft` */
  visitDrafts?: ChartDraft[];
  onOpenDraft?: (entryType: string) => void;
}

const btn = 'min-h-[40px] rounded px-3 text-sm font-medium';

/**
 * Phase 18 M10: "Resume unsaved vitals from 10:42 AM". Shown for the open form's draft, or,
 * while no form is open, for each draft kept for this visit.
 */
const ChartDraftBanner: React.FC<ChartDraftBannerProps> = ({ pendingDraft, onResume, onDiscard, visitDrafts = [], onOpenDraft }) => {
  if (pendingDraft) {
    return (
      <Banner
        variant="info"
        data-testid="chart-draft-banner"
        action={(
          <div className="flex gap-2">
            <button type="button" onClick={onResume} className={`${btn} bg-slate-800 text-white hover:bg-slate-700`}>Resume</button>
            <button type="button" onClick={onDiscard} className={`${btn} border border-slate-300 bg-white text-slate-800 hover:bg-slate-50`}>Discard</button>
          </div>
        )}
      >
        Resume unsaved {label(pendingDraft.entryType)} from {formatDraftTime(pendingDraft.savedAt)}?
      </Banner>
    );
  }
  // Edit drafts are offered when that entry is edited again, not in this list
  const listed = visitDrafts.filter((d) => !isEditDraft(d.entryType));
  if (!listed.length || !onOpenDraft) return null;
  return (
    <div className="space-y-2" data-testid="chart-draft-list">
      {listed.map((d) => (
        <Banner
          key={`${d.visitId}:${d.entryType}`}
          variant="info"
          action={<button type="button" onClick={() => onOpenDraft(d.entryType)} className={`${btn} bg-slate-800 text-white hover:bg-slate-700`}>Resume</button>}
        >
          Unsaved {label(d.entryType)} from {formatDraftTime(d.savedAt)}
        </Banner>
      ))}
    </div>
  );
};

export default ChartDraftBanner;
